import Link from 'next/link';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { gtmBreadcrumbClick } from 'helpers/utils/gtm-events/gtmInteraction';
import BreadCrumbChevron from 'icons/breadcrumb-chevron';
import { Breadcrumb } from '../../../../../../types/misc/Breadcrumb';

interface ProductBreadcrumbsProps {
  breadcrumbs: Breadcrumb[];
  productName?: string;
}

const ProductBreadcrumbs = ({ breadcrumbs, productName }: ProductBreadcrumbsProps) => {
  const pathname = usePathname();

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://shadesoflight.com',
      },
      ...breadcrumbs.map((breadcrumb, index) => ({
        '@type': 'ListItem',
        position: index + 2,
        name: breadcrumb.label?.toLocaleLowerCase().replace(/\b\w/g, (char) => char.toUpperCase()),
        item:
          breadcrumb.slug && typeof breadcrumb.slug === 'string'
            ? breadcrumb.slug.startsWith('//') || breadcrumb.slug.startsWith('http')
              ? breadcrumb.slug.startsWith('//')
                ? `https:${breadcrumb.slug}`
                : breadcrumb.slug
              : `https://shadesoflight.com/${breadcrumb.slug}`
            : '',
      })),
      {
        '@type': 'ListItem',
        position: breadcrumbs.length + 2,
        name: productName,
        item: `https://shadesoflight.com${pathname}`,
      },
    ],
  };

  return (
    <>
      <section>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
        ></script>
      </section>
      <nav aria-label="Breadcrumb" className="mt-4 flex lg:mt-8" data-testid="breadcrumbs">
        <ol className="flex flex-wrap items-center gap-2">
          <li className="flex items-center gap-2" aria-label="Home" data-testid="breadcrumb-home">
            <Link
              onClick={() => {
                gtmBreadcrumbClick('Home');
              }}
              href={'/'}
              className="text-xs"
              aria-label="Home"
            >
              Home
            </Link>
            <BreadCrumbChevron />
          </li>
          {breadcrumbs &&
            breadcrumbs.map((breadcrumb, index) => (
              <li
                key={'breadcrumb-' + index}
                className="flex items-center gap-2"
                aria-label={breadcrumb.label}
                data-testid={'breadcrumb-' + breadcrumb.slug}
              >
                <Link
                  onClick={() => {
                    gtmBreadcrumbClick(
                      breadcrumb.label?.toLocaleLowerCase().replace(/\b\w/g, (char) => char.toUpperCase()) as string,
                    );
                  }}
                  href={breadcrumb.slug || ''}
                  className="text-xs"
                  aria-label={breadcrumb.label}
                >
                  {breadcrumb.label?.toLocaleLowerCase().replace(/\b\w/g, (char) => char.toUpperCase())}
                </Link>
                <BreadCrumbChevron />
              </li>
            ))}
          <li className="text-xs" data-testid="breadcrumb-product">
            {productName}
          </li>
        </ol>
      </nav>
    </>
  );
};

export default ProductBreadcrumbs;
